import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatError, MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderBasicPaneLayoutComponent,
  ElderDialogService,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderThemeDirective,
  ElderToastService,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { Vendor } from '../../vendor'
import { VendorService } from '../vendor.service'

@Component({
  selector: 'app-vendor-detail',
  templateUrl: './vendor-detail.component.html',
  styleUrls: ['./vendor-detail.component.scss'],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneTitleComponent,
    ElderPaneContentComponent,
    ElderPaneSubtitleComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatInput,
    MatError,
    ElderValidationErrorDirective,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class VendorDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private readonly _vendor$: BehaviorSubject<Vendor> = new BehaviorSubject(null)
  private _creationMode: boolean
  private route = inject(ActivatedRoute)
  private batchTracingSpecService = inject(VendorService)
  private toastService = inject(ElderToastService)
  private dialogService = inject(ElderDialogService)
  @Input()
  public embedded = false
  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()
  public readonly idValidatorPattern = IdValidator.pattern

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.vendor = this.route.snapshot.data['spec']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.vendor) {
      this.vendor = this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get vendor$(): Observable<Vendor> {
    return this._vendor$.asObservable()
  }

  public get vendor(): Vendor {
    return this._vendor$.getValue()
  }

  @Input()
  public set vendor(vendor: Vendor) {
    if (vendor) {
      this._creationMode = !vendor.id

      this._vendor$.next(vendor)
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.vendor.id) {
      this.batchTracingSpecService.findById(this.vendor.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    this.batchTracingSpecService.update(this.vendor).subscribe({
      next: (updated) => {
        this.vendor = updated
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.dialogService
      .showConfirm({
        title: 'messages.deleting_confirmation_title',
        message: 'messages.deleting_confirmation_msg',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.batchTracingSpecService.delete(this.vendor).subscribe({
            next: (_) => this.close.emit(),
            error: (err) => this.toastService.error('messages.deleting_failed', err),
          })
        }
      })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): Vendor {
    return new Vendor('Neu')
  }
}
